import {
  AttachmentPreview,
  BoxV2 as Box,
  Button,
  Flex,
  MultimediaList,
  MultimediaUploader,
  RoleGuard,
} from 'portal-commons';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import { faFileImage } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import DeleteSampleMultimediaModal from '../DeleteSampleMultimediaModal';
import UploadSizeExceededModal from '../UploadSizeExceededModal';
import { getSupportedFileFormats } from '../../apis';
import {
  deleteAttachmentApi,
  downloadAttachmentApi,
  downloadAttachmentToDesktop,
  getCampaignAttachmentsApi,
  uploadAttachmentForCampaignApi,
} from '../../apiServices';
import {
  ATTACHMENT_FOLDER_MMS,
  MAX_FILE_SIZE,
  MAX_SAMPLE_MULTIMEDIA_COUNT,
} from '../../constants';
import { Attachment, FileFormats } from '../../types';
import { AttachmentFolder } from '@/utils/types';

interface SampleMultimediaSectionProps {
  readonly?: boolean;
  campaignUid: string;
}

enum ModalType {
  None = 'none',
  SizeExceeded = 'sizeExceeded',
  PreviewAttachment = 'previewAttachment',
  DeleteAttachment = 'deleteAttachment',
}

const SampleMultimediaSection: FunctionComponent<
  SampleMultimediaSectionProps
> = ({ readonly, campaignUid }) => {
  const [supportedFileFormats, setSupportedFileFormats] = useState<FileFormats>(
    {}
  );
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [upload, setUpload] = useState<File>();
  const [activeIndex, setActiveIndex] = useState(-1);
  const [previewCaches, setPreviewCaches] = useState<Record<string, File>>({});
  const [modalType, setModalType] = useState(ModalType.None);

  const activeAttachment = attachments[activeIndex];
  const previewFile = previewCaches[activeAttachment?.uuid];

  const handleUploadChange = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setUpload(undefined);
      setModalType(ModalType.SizeExceeded);
    } else {
      setUpload(file);
    }
  };

  const handleUpload = async () => {
    if (upload) {
      setLoading(true);
      try {
        const attachment = await uploadAttachmentForCampaignApi(
          campaignUid,
          ATTACHMENT_FOLDER_MMS,
          upload
        );
        setAttachments((prev) => [...prev, attachment]);
        setUpload(undefined);
        const newPreviewCaches = { ...previewCaches };
        newPreviewCaches[attachment.uuid] = upload;
        setPreviewCaches(newPreviewCaches);
      } catch (e) {
        // error handling here
      }
      setLoading(false);
    }
  };

  const handleDownload = async (index: number) => {
    await downloadAttachmentToDesktop(
      attachments[index].uuid,
      attachments[index].fileName
    );
  };

  const handleSelect = async (index: number) => {
    setActiveIndex(index);
    setModalType(ModalType.PreviewAttachment);
    const { uuid, fileName, mimeType } = attachments[index];
    if (!previewCaches[uuid]) {
      const response = await downloadAttachmentApi(uuid);
      const newPreviewCaches = { ...previewCaches };
      newPreviewCaches[uuid] = new File([response], fileName, {
        type: mimeType,
      });
      setPreviewCaches(newPreviewCaches);
    }
  };

  const handleDelete = (index: number) => {
    setActiveIndex(index);
    setModalType(ModalType.DeleteAttachment);
  };

  const handleDeleteSubmit = async () => {
    const newAttachments = [...attachments];
    newAttachments.splice(activeIndex, 1);
    setAttachments(newAttachments);
    setActiveIndex(-1);
    try {
      const success = await deleteAttachmentApi(activeAttachment.uuid);
      if (success) {
        setModalType(ModalType.None);
      } else {
        setAttachments(attachments);
        setActiveIndex(activeIndex);
      }
    } catch (e) {
      // rollback on error
      setAttachments(attachments);
      setActiveIndex(activeIndex);
    }
  };

  useEffect(() => {
    (async () => {
      const response = await getSupportedFileFormats(
        AttachmentFolder.SampleMultimedia
      );
      if (response) {
        setSupportedFileFormats(response);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const response = await getCampaignAttachmentsApi(
        campaignUid,
        ATTACHMENT_FOLDER_MMS
      );
      setAttachments(response);
    })();
  }, [campaignUid]);

  return (
    <Grid item xs={12}>
      <div className="details-heading" style={{ borderBottom: 'none' }}>
        <Flex sx={{ alignItems: 'center' }}>
          <Box sx={{ mr: 'xs' }}>
            <FontAwesomeIcon icon={faFileImage} size="xl" color="#49535D" />
          </Box>
          <h3 className="heading1">Sample Multimedia</h3>
        </Flex>
        <Box
          sx={{
            marginTop: '8px',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '16px',
          }}
        >
          Provides an area to upload sample MMS content. Not intended for
          call-to-action, terms and conditions, or privacy policy information.
        </Box>
      </div>
      <Grid container spacing={4}>
        {!readonly && (
          <RoleGuard feature="campaignDetail.addSampleMultimedia">
            <Grid
              item
              xs={6}
              data-testid="campaignDetailSampleMultimediaUploader"
            >
              <MultimediaUploader
                disabled={attachments.length >= MAX_SAMPLE_MULTIMEDIA_COUNT}
                disabledText={`Max ${MAX_SAMPLE_MULTIMEDIA_COUNT} uploads per campaign is allowed`}
                supportedMimeTypes={supportedFileFormats}
                onChange={handleUploadChange}
              />
            </Grid>
          </RoleGuard>
        )}
        <Grid item xs={6} style={{ minHeight: '307px' }}>
          <RoleGuard
            feature="campaignDetail.deleteSampleMultimedia"
            render={(authorized) => (
              <MultimediaList
                data-testid="campaignDetailSampleMultimediaFiles"
                title="Sample Multimedia Files"
                editable={authorized && !readonly}
                loading={
                  modalType === ModalType.PreviewAttachment && !previewFile
                }
                attachments={attachments}
                onDownload={handleDownload}
                onSelect={handleSelect}
                onDelete={handleDelete}
              />
            )}
          />
        </Grid>
        {attachments.length < MAX_SAMPLE_MULTIMEDIA_COUNT && !readonly && (
          <RoleGuard feature="campaignDetail.addSampleMultimedia">
            <Grid
              item
              xs={12}
              className="flex-centered"
              style={{ justifyContent: 'center' }}
            >
              <Button
                data-testid="campaignDetailSampleMultimediaFilesAddButton"
                disabled={!upload || loading}
                variant="standard"
                shape="square"
                color="primary"
                size="small"
                onClick={handleUpload}
              >
                {loading ? 'Processing' : '+ Add Sample Multimedia'}
              </Button>
            </Grid>
          </RoleGuard>
        )}
        {modalType === ModalType.PreviewAttachment && previewFile && (
          <AttachmentPreview
            file={previewFile}
            mimeType={activeAttachment.mimeType}
            onClose={() => setModalType(ModalType.None)}
            data-testid="campaignDetailSampleMultimediaBackdrop"
          />
        )}
      </Grid>
      <UploadSizeExceededModal
        open={modalType === ModalType.SizeExceeded}
        onClose={() => setModalType(ModalType.None)}
      />
      <DeleteSampleMultimediaModal
        open={modalType === ModalType.DeleteAttachment}
        fileName={activeAttachment?.fileName || ''}
        onCancel={() => setModalType(ModalType.None)}
        onSubmit={handleDeleteSubmit}
      />
    </Grid>
  );
};

export default SampleMultimediaSection;
